export const VOICE_CHAT_SUBPROTOCOL = 'cobli.voice.v1';
export const VOICE_CHAT_PING_INTERVAL_MS = 25_000;
export const VOICE_CHAT_CONNECT_TIMEOUT_MS = 15_000;

const NETWORK_DROP_CLOSE_CODE = 1006;

const SERVER_ERROR_REASONS = ['device_busy', 'camera_offline', 'invalid_device', 'unsupported_device', 'internal_error', 'quota_exceeded', 'vendor_closed'] as const;

/** Erros que podem chegar DEPOIS do accepted: a chamada existia e foi cortada, então viram endReason. */
const MID_CALL_ERROR_REASONS = ['quota_exceeded', 'vendor_closed'] as const;

export type VoiceChatServerErrorReason = (typeof SERVER_ERROR_REASONS)[number];
export type VoiceChatErrorReason = VoiceChatServerErrorReason | 'connection_failed' | 'connect_timeout';
export type VoiceChatEndReason = 'local' | 'server' | 'network' | (typeof MID_CALL_ERROR_REASONS)[number];

interface VoiceChatServerMessage {
  type?: string;
  value?: string;
  reason?: string;
}

export interface VoiceChatSocketCallbacks {
  onAccepted: () => void;
  /** Fired at most once, only when the call never reached (or never left) the accepted state. */
  onError: (reason: VoiceChatErrorReason) => void;
  /** Fired at most once, only after the call was accepted. */
  onEnded: (reason: VoiceChatEndReason) => void;
  /** O aviso "iniciando chamada" terminou na cabine — só a partir daqui o TX é aceito. */
  onPreAudioDone?: () => void;
  /** Eco do servidor confirmando a mudança de mute — é ele quem destrava o TX de novo. */
  onMuteChanged?: (muted: boolean) => void;
  /** Frame G.711 A-law cru (320 B = 40 ms) vindo da câmera. Só dispara após accepted. */
  onAudioFrame?: (frame: ArrayBuffer) => void;
}

interface VoiceChatSocketOptions {
  host: string;
  getToken: () => Promise<string | null>;
  /** Test seam — defaults to the native WebSocket constructor. */
  createWebSocket?: (url: string, protocols: string[]) => WebSocket;
}

const toServerErrorReason = (reason?: string): VoiceChatErrorReason => {
  return SERVER_ERROR_REASONS.includes(reason as VoiceChatServerErrorReason) ? (reason as VoiceChatServerErrorReason) : 'internal_error';
};

/**
 * Manages one voice-chat call over WebSocket against the pooler:
 * connecting the socket IS opening the call on the vendor; closing it IS hanging up.
 * Controle em JSON texto; áudio em frames binários G.711 A-law de 320 B (40 ms @ 8 kHz),
 * nos dois sentidos, sem envelope — o pooler embrulha no formato da Streamax.
 */
export class VoiceChatSocket {
  private readonly options: VoiceChatSocketOptions;
  private ws: WebSocket | null = null;
  private callbacks: VoiceChatSocketCallbacks | null = null;
  private accepted = false;
  private settled = false;
  /** `disconnect()` chegou antes do socket existir — ver a checagem pós-`getToken()` em `connect`. */
  private aborted = false;
  /** TX liberado só após `pre_audio_done` — o pooler descarta (não enfileira) frames antes disso. */
  private preAudioDone = false;
  /** Trava na hora do `mute()`; só destrava no eco `state: unmuted` do servidor. */
  private muted = false;
  private serverErrorReason: VoiceChatErrorReason | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(options: VoiceChatSocketOptions) {
    this.options = options;
  }

  public connect = async (deviceId: string, callbacks: VoiceChatSocketCallbacks): Promise<void> => {
    this.callbacks = callbacks;

    // O socket é dono do próprio canal de erro: renovação de token que rejeita vira
    // callback, nunca rejeição da promise — consumidor nenhum precisa de catch para
    // sair do estado "conectando".
    let token: string | null;
    try {
      token = await this.options.getToken();
    } catch {
      if (!this.aborted) {
        this.settle(() => callbacks.onError('connection_failed'));
      }
      return;
    }

    // A renovação de token é assíncrona: fechar o dialog, desligar, desmontar ou trocar de device
    // nessa janela deve cancelar a tentativa. Abrir o socket aqui abriria a chamada no vendor sem
    // UI para encerrá-la, e o ping manteria a sessão viva — o device ficaria preso ocupado.
    if (this.aborted) {
      return;
    }

    if (!token) {
      this.settle(() => callbacks.onError('connection_failed'));
      return;
    }

    const wsHost = this.options.host.replace(/^http/, 'ws');
    const url = `${wsHost}/cameras/voice/devices/${deviceId}`;
    const createWebSocket = this.options.createWebSocket ?? ((wsUrl, protocols) => new WebSocket(wsUrl, protocols));

    try {
      this.ws = createWebSocket(url, [VOICE_CHAT_SUBPROTOCOL, token]);
    } catch {
      this.settle(() => callbacks.onError('connection_failed'));
      return;
    }

    // Sem isso os frames da câmera chegariam como Blob, que exige leitura assíncrona.
    this.ws.binaryType = 'arraybuffer';

    // Limita a espera pelo `accepted` a 15s: o vendor tem uma janela de ~30s para atender, e sem
    // esse corte o socket ficaria aberto indefinidamente com a UI presa em "conectando".
    this.connectTimeout = setTimeout(this.handleConnectTimeout, VOICE_CHAT_CONNECT_TIMEOUT_MS);
    this.ws.onmessage = this.handleMessage;
    this.ws.onclose = this.handleClose;
  };

  public disconnect = (): void => {
    this.aborted = true;
    if (this.settled && !this.ws) {
      return;
    }
    const wasAccepted = this.accepted;
    const ws = this.ws;
    this.teardown();
    ws?.close(1000);
    if (wasAccepted) {
      this.settle(() => this.callbacks?.onEnded('local'));
    } else {
      this.settled = true;
    }
  };

  public isActive = (): boolean => {
    return this.ws !== null && !this.settled;
  };

  private handleConnectTimeout = () => {
    const ws = this.ws;
    this.teardown();
    ws?.close(1000);
    this.settle(() => this.callbacks?.onError('connect_timeout'));
  };

  /** Envia um frame G.711 A-law (320 B). Descarta em silêncio fora da janela em que o pooler aceita TX. */
  public sendAudioFrame = (frame: ArrayBuffer): void => {
    if (!(this.accepted && this.preAudioDone) || this.muted || this.settled || !this.ws) {
      return;
    }
    this.ws.send(frame);
  };

  /** Trava o TX imediatamente e pede mute ao servidor (que para o encaminhamento). */
  public mute = (): void => {
    this.muted = true;
    this.ws?.send(JSON.stringify({ type: 'mute' }));
  };

  /** Pede unmute — o TX só destrava quando o eco `state: unmuted` voltar. */
  public unmute = (): void => {
    this.ws?.send(JSON.stringify({ type: 'unmute' }));
  };

  private handleMessage = (event: MessageEvent) => {
    if (typeof event.data !== 'string') {
      if (this.accepted) {
        this.callbacks?.onAudioFrame?.(event.data as ArrayBuffer);
      }
      return;
    }

    let message: VoiceChatServerMessage;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }

    if (message.type === 'state' && message.value === 'pre_audio_done') {
      this.preAudioDone = true;
      this.callbacks?.onPreAudioDone?.();
      return;
    }

    if (message.type === 'state' && (message.value === 'muted' || message.value === 'unmuted')) {
      this.muted = message.value === 'muted';
      this.callbacks?.onMuteChanged?.(this.muted);
      return;
    }

    if (message.type === 'state' && message.value === 'accepted') {
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
        this.connectTimeout = null;
      }
      this.accepted = true;
      this.pingInterval = setInterval(this.sendPing, VOICE_CHAT_PING_INTERVAL_MS);
      this.callbacks?.onAccepted();
      return;
    }

    if (message.type === 'error') {
      this.serverErrorReason = toServerErrorReason(message.reason);
    }
  };

  private handleClose = (event: CloseEvent) => {
    const wasAccepted = this.accepted;
    const serverErrorReason = this.serverErrorReason;
    this.teardown();
    this.ws = null;

    if (serverErrorReason) {
      // Depois do accepted, quota estourada / vendor caído são fins de chamada, não falhas
      // de conexão: a UI mostra "chamada encerrada (motivo)", não "não foi possível conectar".
      if (wasAccepted && MID_CALL_ERROR_REASONS.includes(serverErrorReason as (typeof MID_CALL_ERROR_REASONS)[number])) {
        this.settle(() => this.callbacks?.onEnded(serverErrorReason as VoiceChatEndReason));
        return;
      }
      this.settle(() => this.callbacks?.onError(serverErrorReason));
      return;
    }
    if (!wasAccepted) {
      this.settle(() => this.callbacks?.onError('connection_failed'));
      return;
    }
    this.settle(() => this.callbacks?.onEnded(event.code === NETWORK_DROP_CLOSE_CODE ? 'network' : 'server'));
  };

  private sendPing = () => {
    this.ws?.send(JSON.stringify({ type: 'ping' }));
  };

  private settle = (notify: () => void) => {
    if (this.settled) {
      return;
    }
    this.settled = true;
    notify();
  };

  private teardown = () => {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
    if (this.ws) {
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws = null;
    }
  };
}

export default VoiceChatSocket;
